import { mailDeeplinkModuleName, answersDeeplinkModuleName } from './strings.locstring.json';
import loc from 'owa-localize';
// Order is important, do not mess with it :)
import * as React from 'react';
import type { AppBootstrapOptions } from 'owa-app-module/lib/types/AppBootstrapOptions';
import initializeMailModuleState from 'mail-bootstrap-config/lib/initializeMailModuleState';
import DeepLinkModule from './components/DeepLinkModule';
import { getMailRouterOptions } from './routing/deepLinkMailRoutes';
import { OwaWorkload, Module } from 'owa-workloads';
import { controlIconRegistration } from 'owa-control-icons';
import { mailIconsRegistration } from 'owa-mail-icons';
import { addinIconsRegistration } from 'owa-addins-icons';
import { surfaceActionIconsRegistration } from 'owa-surface-action-icons';
import { getAnalyticOptions } from 'owa-shared-analytics';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import {
    lazySetupMailDeeplinkCommonPostRender,
    lazySetupMailModuleCommonPostRender,
} from 'owa-mail-post-boot/lib/deeplinkIndex';
import initializeMailDeeplinkModuleState from './utils/initializeMailDeeplinkModuleState';
import isPopoutV2 from './utils/isPopoutV2';
import type { BootStrategies } from 'owa-shared-start';

// Regex to extract subRoute from opx-deepLink path
// The opx-deepLink path can be of these formats:
// /mail/deeplink/<subRoute> OR  /mail/opxdeeplink/<subRoute> OR
// /mail/deeplink/<subRoute>/ OR  /mail/opxdeeplink/<subRoute>/
const SUB_ROUTE_REGEX = /\/(opx)*deeplink\/([a-z]*)(\/)*/i;

export function getMailDeepLinkBootstrapOptions(
    sessionPromise: Promise<SessionData | undefined>,
    strategies?: BootStrategies
): Promise<AppBootstrapOptions> {
    const subRoute = getSubRoute();
    const isLeanBootstrapEnabled = isPopoutV2();
    let postLazyAction;
    let initializeState;
    let getModuleName;

    // saveAndEdit is a deeplink that saves an attachment to OneDrive then opens it in that tab.
    // searchAnswer is a deeplink that renders 3S Answers on OPX used by Outlook Desktop
    // Since we do not need any mail UI for these, or to make any other service calls we skip
    // initializing everything except the notification channel, which is initialized elsewhere.
    if (subRoute !== 'saveAndEdit' && subRoute !== 'searchAnswer') {
        postLazyAction = isLeanBootstrapEnabled
            ? lazySetupMailDeeplinkCommonPostRender
            : lazySetupMailModuleCommonPostRender;
    }

    // initializeState will unnecessarily load conversations and other resources which are not
    // required in case of SearchAnswers so we are sending just a function that does nothing
    if (subRoute === 'searchAnswer') {
        initializeState = () => {};
        getModuleName = () => loc(answersDeeplinkModuleName);
    } else {
        initializeState = isLeanBootstrapEnabled
            ? initializeMailDeeplinkModuleState
            : initializeMailModuleState;
        getModuleName = () => loc(mailDeeplinkModuleName);
    }

    return Promise.resolve({
        initializeState,
        sessionPromise,
        strategies,
        routerOptions: getMailRouterOptions(),
        getModuleName,
        workload: OwaWorkload.Mail,
        analyticsOptions: getAnalyticOptions(),
        iconFonts: [
            controlIconRegistration,
            mailIconsRegistration,
            addinIconsRegistration,
            surfaceActionIconsRegistration,
        ],
        postLazyAction,
        appModuleProps: {
            module: Module.MailDeepLink,
            renderModule: () => <DeepLinkModule subRoute={subRoute} />,
        },
    });
}

function getSubRoute() {
    const pathname = window?.location?.pathname;
    if (pathname) {
        const result = pathname.match(SUB_ROUTE_REGEX);
        return !!result ? result[2] : '';
    }
    return '';
}
